import { authenticatedClient } from '../lib/client';
import { CALLS_ENDPOINTS } from './calls.utils';
import type { ApiResponse } from '../lib/types';
import type { Call, CreateCallRequest, PaginatedCallsResponse } from './calls.types';

interface PaginationParams {
    skip?: number;
    limit?: number;
}

interface CallFilterRequest {
    start_date?: string | null;
    end_date?: string | null;
    id?: string[] | null;
    assistant_id?: string[] | null;
    phone_number_id?: string[] | null;
    phone_number?: string[] | null;
    customer_phone_number?: string[] | null;
    ended_reason?: string[] | null;
    type?: string[] | null;
}

export const getAllCalls = async (
    paginationParams: PaginationParams = {},
    filters: CallFilterRequest = {}
): Promise<{
    data: Call[];
    total: number;
}> => {
    try {
        const { skip = 0, limit = 10 } = paginationParams;
        
        // Query params for pagination
        const queryParams = { skip, limit };
        
        // Body for filters
        const requestBody: CallFilterRequest = {
            start_date: filters.start_date || null,
            end_date: filters.end_date || null,
            id: filters.id || null,
            assistant_id: filters.assistant_id || null,
            phone_number_id: filters.phone_number_id || null,
            phone_number: filters.phone_number || null,
            customer_phone_number: filters.customer_phone_number || null,
            ended_reason: filters.ended_reason || null,
            type: filters.type || null,
        };
        
        // Use POST request with filters in body
        const response = await authenticatedClient.post<PaginatedCallsResponse>(
            CALLS_ENDPOINTS.GET_ALL, 
            requestBody,
            { params: queryParams }
        );
        
        return { 
            data: response.data.calls,
            total: response.data.total_count
        };
    } catch (error: any) {
        const errorDetail = error.response?.data?.detail || '';
        const errorMessage = errorDetail
            .replace(/status_code: \d+, body: /, '')
            .replace(/[{}'\"]/g, '')
            .replace(/message: /, '')
            .replace(/, error: .*$/, '')
            .trim();

        throw new Error(errorMessage || 'Failed to fetch calls');
    }
};

export const getCallById = async (callId: string): Promise<ApiResponse<Call>> => {
    try {
        const response = await authenticatedClient.get(CALLS_ENDPOINTS.GET_BY_ID(callId));
        return response.data;
    } catch (error: any) {
        const errorDetail = error.response?.data?.detail || '';
        const errorMessage = errorDetail
            .replace(/status_code: \d+, body: /, '')
            .replace(/[{}'\"]/g, '')
            .replace(/message: /, '')
            .replace(/, error: .*$/, '')
            .trim();

        return { error: errorMessage || `Failed to fetch call details for ID: ${callId}` };
    }
};

export const createCall = async (payload: CreateCallRequest): Promise<ApiResponse> => {
    try {
        const response = await authenticatedClient.post(CALLS_ENDPOINTS.CREATE, payload);
        return { data: response.data };
    } catch (error: any) {
        // Get the error message from the response
        const errorDetail = error.response?.data?.detail || '';
        const errorMessage = errorDetail
            .replace(/status_code: \d+, body: /, '')  // Remove status code prefix
            .replace(/[{}'\"]/g, '')  // Remove brackets, single and double quotes
            .replace(/message: /, '')  // Remove message prefix
            .replace(/, error: .*$/, '')  // Remove error suffix
            .trim();

        return { error: errorMessage || 'Failed to create call' };
    }
};